import { useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import FlashcardBlock from './FlashcardBlock';
import { parseFlashcardContent, isValidFlashcard } from '../utils/parseFlashcardContent';
import SuggestionButtons from './ai-chat/SuggestionButtons';
import ComicBlock, { parseComicContent } from './ComicBlock';
import rehypeCitation, { rehypeArticleCitation } from '../utils/rehypeCitation';
import CitationPill from './ai-chat/CitationPill';

/**
 * ReedleMarkdown - Extended markdown renderer for AI chat messages
 *
 * Extends RichMarkdown to support custom reedle:* code blocks:
 * - reedle:flashcard - Rendered as FlashcardBlock component
 * - reedle:suggestions - Rendered as SuggestionButtons component (when enableSuggestions is true)
 * - reedle:comic - Rendered as ComicBlock component (image artifact card)
 *
 * @param {string} children - Markdown content
 * @param {string} articleId - Source article ID for card linking
 * @param {number} messageIndex - Message index in conversation
 * @param {function} onSaveCard - Callback when user wants to save a card
 * @param {Array} savedCards - Array of already saved cards (for checking isSaved state)
 * @param {boolean} enableSuggestions - Whether to render suggestion blocks (default: false)
 * @param {function} onSuggestionClick - Callback when a suggestion button is clicked
 * @param {boolean} suggestionsDisabled - Whether suggestion buttons should be disabled (e.g., while sending)
 * @param {function} onOpenLightbox - Callback to open image lightbox (for comic blocks)
 * @param {Array} citationSources - Source objects for citation rendering (enables [n] pill rendering)
 * @param {function} onCitationClick - Callback when citation pill is clicked: (number, source) => void
 * @param {string[]} [suggestionsOverride] - Override suggestion list (from refresh)
 * @param {function} [onRefreshSuggestions] - Called when user clicks the refresh icon
 * @param {boolean} [isRefreshingSuggestions] - True while refresh is in progress
 */
export default function ReedleMarkdown({
  children,
  articleId,
  messageIndex,
  onSaveCard,
  savedCards = [],
  enableSuggestions = false,
  onSuggestionClick,
  suggestionsDisabled = false,
  suggestionContext = 'article_chat',
  onOpenLightbox,
  citationSources,
  onCitationClick,
  articleCitationMode = false,
  suggestionsOverride,
  onRefreshSuggestions,
  isRefreshingSuggestions = false,
}) {
  // Check if a card is already saved by comparing front text.
  // useCallback so the reference is stable when savedCards hasn't changed —
  // this lets the components useMemo below avoid unnecessary remounts.
  const isCardSaved = useCallback((front) => {
    if (!savedCards || !Array.isArray(savedCards)) return false;
    return savedCards.some(
      c => c && c.front?.toLowerCase().trim() === front?.toLowerCase().trim()
    );
  }, [savedCards]);

  // Build citation source lookup map (source_number → source object)
  const citationSourceMap = useMemo(() => {
    if (!citationSources?.length) return null;
    const map = new Map();
    for (const s of citationSources) {
      if (s.source_number != null) map.set(String(s.source_number), s);
    }
    return map;
  }, [citationSources]);

  // Build rehype plugins list — include citation plugin when citations are enabled
  const rehypePlugins = useMemo(() => {
    const plugins = [[rehypeKatex, { strict: false }]];
    if (articleCitationMode) {
      plugins.push(rehypeArticleCitation);
    } else if (citationSourceMap) {
      plugins.push(rehypeCitation);
    }
    return plugins;
  }, [citationSourceMap, articleCitationMode]);

  // Memoise the components map so ReactMarkdown receives a stable object reference
  // across re-renders triggered by scroll/parent state updates. Without this,
  // a new components object on every render causes ReactMarkdown to unmount and
  // remount all custom nodes (ComicBlock, FlashcardBlock, etc.), resetting their
  // local state (imageLoaded → false → loading flash on every scroll).
  const components = useMemo(() => ({
    // Render <cite data-citation="n"> as CitationPill (source or block mode)
    ...((citationSourceMap || articleCitationMode) ? {
      // eslint-disable-next-line no-unused-vars -- hast node must not reach a DOM element; dropping the binding spreads node="[object Object]" onto <cite>
      cite({ node, children, ...props }) {
        const num = props['dataCitation'] || props['data-citation'];
        const mode = props['dataCitationMode'] || props['data-citation-mode'] || 'source';
        if (num) {
          const source = mode === 'source' ? citationSourceMap?.get(String(num)) : undefined;
          return (
            <CitationPill
              number={parseInt(num, 10)}
              source={source}
              onClick={onCitationClick}
              mode={mode}
            />
          );
        }
        return <cite {...props}>{children}</cite>;
      },
    } : {}),
    code(props) {
      const { children: codeChildren, className, ...rest } = props;

      // Handle reedle:flashcard blocks
      const flashcardMatch = /language-reedle:flashcard/.exec(className || '');
      if (flashcardMatch) {
        const content = String(codeChildren).replace(/\n$/, '');
        const parsed = parseFlashcardContent(content);

        // Skip invalid cards
        if (!isValidFlashcard(parsed)) {
          // Fall back to regular code block display
          return <code className={className} {...rest}>{codeChildren}</code>;
        }

        const saved = isCardSaved(parsed.front);

        return (
          <FlashcardBlock
            front={parsed.front}
            back={parsed.back}
            isSaved={saved}
            onSave={() => {
              if (onSaveCard) {
                onSaveCard({
                  front: parsed.front,
                  back: parsed.back,
                  articleId,
                  messageIndex,
                });
              }
            }}
          />
        );
      }

      // Handle reedle:suggestions blocks (only when enableSuggestions is true)
      const suggestionsMatch = /language-reedle:suggestions/.exec(className || '');
      if (suggestionsMatch) {
        // If suggestions are not enabled, hide the block entirely
        if (!enableSuggestions) {
          return null;
        }

        const suggestionsText = String(codeChildren).trim();
        const suggestions = suggestionsText
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (suggestions.length === 0) {
          return null;
        }

        // Use override suggestions if available (after refresh)
        const displaySuggestions = suggestionsOverride && suggestionsOverride.length > 0
          ? suggestionsOverride
          : suggestions;

        return (
          <SuggestionButtons
            suggestions={displaySuggestions}
            onSuggestionClick={onSuggestionClick}
            disabled={suggestionsDisabled}
            context={suggestionContext}
            onRefresh={onRefreshSuggestions}
            isRefreshing={isRefreshingSuggestions}
          />
        );
      }

      // Handle reedle:comic blocks
      const comicMatch = /language-reedle:comic/.exec(className || '');
      if (comicMatch) {
        const content = String(codeChildren).replace(/\n$/, '');
        const parsed = parseComicContent(content);

        if (!parsed.url) {
          return <code className={className} {...rest}>{codeChildren}</code>;
        }

        return (
          <ComicBlock
            url={parsed.url}
            model={parsed.model}
            style={parsed.style}
            credits={parsed.credits}
            onOpenLightbox={onOpenLightbox}
          />
        );
      }

      // Regular code (both inline and block)
      return <code className={className} {...rest}>{codeChildren}</code>;
    },
    pre(props) {
      const { children: preChildren } = props;
      const className = preChildren?.props?.className || '';

      // Unwrap reedle:flashcard blocks (don't wrap in <pre>)
      if (className.includes('language-reedle:flashcard')) {
        return preChildren;
      }

      // Unwrap reedle:suggestions blocks (don't wrap in <pre>)
      if (className.includes('language-reedle:suggestions')) {
        return preChildren;
      }

      // Unwrap reedle:comic blocks (don't wrap in <pre>)
      if (className.includes('language-reedle:comic')) {
        return preChildren;
      }

      // Normal pre wrapper for block code
      return <pre {...props} />;
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isCardSaved, onSaveCard, articleId, messageIndex, enableSuggestions,
    onSuggestionClick, suggestionsDisabled, suggestionContext, onOpenLightbox,
    citationSourceMap, articleCitationMode, onCitationClick,
    suggestionsOverride, onRefreshSuggestions, isRefreshingSuggestions]);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {children}
    </ReactMarkdown>
  );
}
